import type { Lesson } from "@/lib/lecturehub/types";

/** Generic over the lesson shape: the public course page walks `Lesson` rows
 *  and the learn runner walks `LessonWithContent` ones, through this one
 *  function. Defaults to `Lesson` so existing callers read unchanged. */
export type LessonRefRow<L = Lesson> = {
  lesson: L;
  sectionName: string | null;
  sectionIndex: number | null;
  indexWithinSection: number;
  absoluteIndex: number;
};

/**
 * A section can be `draft` even inside a published course, and the backend
 * returns it anyway (CourseSection.status is documented as unfiltered) — so
 * visibility is gated here, once, for every consumer. Null-permissive like
 * `canView`: only an explicit non-"published" status hides a section.
 */
/**
 * A SECTIONED course exposes its lessons ONLY under `course.sections[].lessons`
 * — `course.lessons` comes back empty for it, it is not a flattened mirror.
 * Verified on production 2026-09-13: Elementum Academy 193 flat + 345 in
 * sections, Avant2Go 0 flat + 28 in sections (both its courses are sectioned).
 * Anything that walks a course's lessons must read BOTH shapes or it silently
 * measures a fraction of the catalog — which is exactly how the lesson-thumbnail
 * change was first mis-measured as 100 lessons instead of 538.
 */
export function publishedSections<S extends { status?: string | null }>(
  course: { sections?: (S | null)[] | null } | null | undefined,
): S[] {
  return (course?.sections ?? []).filter(
    (s): s is S => s != null && (s.status == null || s.status === "published"),
  );
}

/**
 * One level down from `publishedSections`: a LESSON's publication state lives
 * on its subtype (Lecture/Quiz/Article/Assignment.status), and the public
 * `lessons` fields resolve every item "regardless of publication status"
 * (backend CourseType/CourseSectionType → allItems(), read at source
 * 2026-08-23 — roadmap O16). So a draft lesson inside a published section
 * reaches guests unless it is dropped here. Same null-permissive rule: a
 * selection that did not fetch the status (or a subtype without one) passes.
 */
/** What the check needs — the `LessonStatus` fragment's shape, which a full
 *  `Lesson` also satisfies. Structural so the thin card/checkout selections
 *  go through the same function. */
type WithStatus = { status?: string | null } | null | undefined;
export type LessonStatusShape = {
  refType?: string | null;
  lecture?: WithStatus;
  quiz?: WithStatus;
  article?: WithStatus;
  assignment?: WithStatus;
};

export function lessonStatus(lesson: LessonStatusShape): string | null | undefined {
  switch (lesson.refType) {
    case "lecture":
      return lesson.lecture?.status;
    case "quiz":
      return lesson.quiz?.status;
    case "article":
      return lesson.article?.status;
    case "assignment":
      return lesson.assignment?.status;
    default:
      return undefined;
  }
}

export function publishedLessons<L extends LessonStatusShape>(
  lessons: (L | null)[] | null | undefined,
): L[] {
  return (lessons ?? []).filter((l): l is L => {
    if (l == null) return false;
    const status = lessonStatus(l);
    return status == null || status === "published";
  });
}

/**
 * Flatten a course's curriculum into a single ordered list, regardless of
 * whether it's sectioned or flat. Used by the lesson detail route for
 * prev/next nav and sidebar rendering. Published lessons only — a draft
 * lesson id is simply not in the curriculum, so the player 404s it.
 */
export function flattenCurriculum<L extends LessonStatusShape>(module: {
  course?:
    | {
        childrenType?: string | null;
        lessons?: (L | null)[] | null;
        sections?:
          | ({
              name?: string | null;
              status?: string | null;
              lessons?: (L | null)[] | null;
            } | null)[]
          | null;
      }
    | null
    | undefined;
}): LessonRefRow<L>[] {
  const course = module.course;
  if (!course) return [];

  const out: LessonRefRow<L>[] = [];
  if (course.childrenType === "sections") {
    const sections = publishedSections(course);
    sections.forEach((section, sIdx) => {
      publishedLessons(section.lessons).forEach((lesson, lIdx) => {
        out.push({
          lesson,
          sectionName: section.name ?? null,
          sectionIndex: sIdx,
          indexWithinSection: lIdx,
          absoluteIndex: out.length,
        });
      });
    });
  } else {
    publishedLessons(course.lessons).forEach((lesson, lIdx) => {
      out.push({
        lesson,
        sectionName: null,
        sectionIndex: null,
        indexWithinSection: lIdx,
        absoluteIndex: out.length,
      });
    });
  }
  return out;
}

export function lessonTitle(lesson: Lesson): string {
  switch (lesson.refType) {
    case "lecture":
      return lesson.lecture?.name ?? "Lecture";
    case "quiz":
      return lesson.quiz?.name ?? "Quiz";
    case "article":
      return lesson.article?.name ?? "Article";
    case "assignment":
      return lesson.assignment?.name ?? "Assignment";
    default:
      return "Item";
  }
}

export function lessonFree(lesson: Lesson): boolean {
  switch (lesson.refType) {
    case "lecture":
      return !!lesson.lecture?.free;
    case "quiz":
      return !!lesson.quiz?.free;
    case "article":
      return !!lesson.article?.free;
    case "assignment":
      return !!lesson.assignment?.free;
    default:
      return false;
  }
}
